import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { queries } from 'services/queries';
import { buildTypeSource } from 'views/Conversation/Sider/ToDoBundle/todoBusinessUtils';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';

interface UseTicketParams {
  buyerId: string;
  storeId: string;
  tradeBuyerId: string;
}

// 状态排序优先级：处理中(2) > 待处理(4) > 待认领(1) > 已完成(3)，同状态内按创建时间正序
const STATUS_ORDER: Record<number, number> = { 2: 0, 4: 1, 1: 2, 3: 3 };

const sortTicketList = (list: Todo.TodoInstance[]) => {
  return [...list].sort((a, b) => {
    const statusA = STATUS_ORDER[Number(a.todoStatus)] ?? 99;
    const statusB = STATUS_ORDER[Number(b.todoStatus)] ?? 99;
    if (statusA !== statusB) return statusA - statusB;
    const timeA = Number(a.createTime);
    const timeB = Number(b.createTime);
    return timeA - timeB;
  });
};

export const useTicket = (params: UseTicketParams) => {
  const { buyerId, storeId, tradeBuyerId } = params;
  const queryClient = useQueryClient();
  const queryKey = queries.tickets.list({
    buyerId,
    storeId,
    tradeBuyerId,
  }).queryKey;

  const { data, isLoading } = useQuery({
    ...queries.tickets.list({ buyerId, storeId, tradeBuyerId }),
  });

  const ticketList = useMemo(() => {
    return sortTicketList(data?.todoInstanceDetails || []);
  }, [data?.todoInstanceDetails]);

  const appendTicketToList = useCallback(
    (newTicket: Todo.TodoInstance) => {
      queryClient.setQueryData<{ todoInstanceDetails: Todo.TodoInstance[] }>(queryKey, (old) => {
        const baseList = old?.todoInstanceDetails || [];
        const deduped = baseList.filter((ticket) => ticket.id !== newTicket.id);
        return {
          ...old,
          todoInstanceDetails: sortTicketList([...deduped, newTicket]),
        };
      });
    },
    [queryClient, queryKey],
  );

  return {
    isLoading,
    total: ticketList.length,
    ticketList,
    appendTicketToList,
  };
};

export const useTicketTask = (storeId: string) => {
  const { getTypeLabel } = useTypeLabel();

  const { data, isLoading } = useQuery({
    ...queries.tickets.task(storeId),
  });

  const typeSource = useMemo(
    () => buildTypeSource(data?.policies, getTypeLabel as (type: Todo.Type) => string | number),
    [data?.policies, getTypeLabel],
  );

  return {
    isLoading,
    task: data?.task,
    policies: data?.policies || [],
    typeSource,
  };
};
